import { Component, ElementRef, EventEmitter, Inject, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EmployerProfile } from '@core/models/leave-admin/employers/employer-profile.model';
import { EmployerSettingsPost } from '@core/models/leave-admin/employers/employer-settings-post.model';
import { AvatarService, ErrorService, LayoutService, SnackbarService } from '@core/services';
import { ManageEmployersService } from '@core/services/manage-employers.service';
import { FormBuilder, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject, Observable } from 'rxjs';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { StiiraError } from '@core/models';
import { finalize, takeUntil } from 'rxjs/operators';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';

@Component({
    selector: 'app-employer-settings',
    templateUrl: './employer-settings.component.html',
    styleUrls: ['./employer-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EmployerSettingsComponent implements OnInit {
  static readonly componentName = 'EmployerSettingsComponent';
  
  @ViewChild('imgFileInput') fileInput: ElementRef;
  @ViewChild('uploadButton', { read: ElementRef }) private uploadButton: ElementRef<HTMLButtonElement>;
  @ViewChild(ImageCropperComponent, { read: ElementRef }) private logoCropper: ElementRef<HTMLElement>;
  @Output() isEditing = new EventEmitter<boolean>();

  public form: UntypedFormGroup;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean>;
  public logoChangedEvent: Event = null;
  public employerLogoImage: string = null;
  public setEmployerLogo: string = null;
  
  public position = position;
  public onDragEnded = onDragEnded;

  private subdomainNamePattern: string = '(^[a-zA-Z0-9][a-zA-Z0-9-]*)[a-zA-Z0-9]+$';
  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  get formIsValid(): boolean {
    return this.form.valid;
  }

  get companyName(): FormControl {
    return this.form.controls.companyName as FormControl;
  }

  get subdomainName(): FormControl {
    return this.form.controls.subdomainName as FormControl;
  }

  get employerLogo(): FormControl {
    return this.form.controls.employerLogo as FormControl;
  }

  get headcount(): FormControl {
    return this.form.controls.headcount as FormControl;
  }

  get allowAccess(): FormControl {
    return this.form.controls.allowAccess as FormControl;
  }

  get allowNewLeaves(): FormControl {
    return this.form.controls.allowNewLeaves as FormControl;
  }

  public isMobile$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      erProfile: EmployerProfile;
      sysText: any;
      unsavedChangesSysText: any;
    },
    private employerSettingsdialogRef: MatDialogRef<EmployerSettingsComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private manageEmployersService: ManageEmployersService,
    private errorService: ErrorService,
    private snackbarService: SnackbarService,
    private avatarService: AvatarService,
    private layoutService: LayoutService
    ) { 
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group({
      companyName: [null, Validators.required],
      subdomainName: [null, Validators.compose([Validators.pattern(this.subdomainNamePattern), Validators.minLength(2)])],
      employerLogo: [null], // holds image file name, also used for file size ms error
      headcount: [null],
      allowAccess: [null],
      allowNewLeaves: [null]
    })
}

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.employerSettingsdialogRef, this.isSubmitting$, () => this.cancel());

    this.form.patchValue({
      companyName: this.data.erProfile.companyName,
      subdomainName: this.data.erProfile.customSubdomainName,
      headcount: this.data.erProfile.headcount,
      allowAccess: this.data.erProfile.allowAccess,
      allowNewLeaves: this.data.erProfile.allowNewLeaves
    });

    if (this.data.erProfile.isTenantEmployer) {
      this.form.controls.subdomainName.setValidators(Validators.required)
      this.form.updateValueAndValidity();
    }

    if (this.data.erProfile.customEmployerLogo) {
      this.setEmployerLogo = this.data.erProfile.customEmployerLogo.image;
      this.employerLogo.setValue(this.data.erProfile.customEmployerLogo.fileName);
    }

    this.setFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setFormErrors() {
    setTimeout(() => {
      this.errorService.setFormModelStateErrors(this.form, this.data.erProfile.modelStateErrors);
      this.formInitValues = { ...this.form.value };
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  public logoChangeEvent(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    if (element.files[0]) {
      this.employerLogo.setValue(element.files[0].name);
      this.logoChangedEvent = event;
    }
  }

  public getEmployerLogo(event: ImageCroppedEvent) {
    this.employerLogoImage = event.base64;
  }

  // the cropper only shows the logo here, but its crop box takes focus with no role; it's drawn on the next render
  public onLogoCropperReady(): void {
    requestAnimationFrame(() => this.logoCropper.nativeElement.querySelector('.ngx-ic-cropper')?.setAttribute('role', 'img'));
  }

  public loadImageFailed() {
    this.clearFile();
    this.snackbarService.open(this.data.sysText.imageLoadError, this.data.sysText.dismiss);
  }

  public clearFile(): void {
    // the pressed button goes with the logo; the upload button appears in its place
    setTimeout(() => this.uploadButton.nativeElement.focus());
    this.logoChangedEvent = null;
    this.employerLogo.setValue(null);
    this.employerLogoImage = null;
    this.setEmployerLogo = null;
  }

  public save(): void {
    const dto: EmployerSettingsPost = {
      employerId: this.data.erProfile.employerId,
      companyName: this.companyName.value,
      subdomainName: this.subdomainName.value,
      employerLogo: this.employerLogoImage ? {
          image: this.employerLogoImage, 
          fileName: this.employerLogo.value 
        } : null,
      headcount: this.headcount.value,
      allowAccess: this.allowAccess.value,
      allowNewLeaves: this.allowNewLeaves.value
    }
      this.manageEmployersService.postEmployerSettings(dto)
        .pipe(
          indicate(this.isSubmitting$),
          finalize(() => { })
        )
        .subscribe((res) => {
          this.employerSettingsdialogRef.close(res);
          // if employer logo was changed, trigger sidenav to update
          if (res && this.employerLogoImage != this.data.erProfile.customEmployerLogo?.image) {
            this.avatarService.updateSidenav();
          }
        }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
  }

  public cancel(): void {
    if (this.noChanges) {
      this.employerSettingsdialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };

    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.employerSettingsdialogRef.close();
        }
      });
  }
}
